import { createContext, useContext, useMemo, useReducer } from "react"
import { produce } from "immer"
import { usePreferences } from "hooks/use-preferences"
import { isEmpty } from "lodash"

export const TradeContext = createContext()

const PREFERENCE_SUBTYPE = "trade"
const EMPTY_ARRAY = []

const DEFAULT_STATE = {
  trades: EMPTY_ARRAY,
  tradedPosition: null,
  tradedPositionField: null,
  actionsBase: null,
  isExpanded: false,
  assetViewGroupBy: null,
}

// Define which fields should be saved as preferences
const PREFERENCE_FIELDS = [
  "isExpanded",
]

function reducer(state, action) {
  return produce(state, draft => {
    switch (action.type) {
      case "addTrade":
        draft.trades.push(action.trade)
        if (action.trade.base && action.trade.field === "activePercentEquity" && !state.actionsBase) {
          draft.actionsBase = action.trade.base
        }
        break
      case "resetTrades":
        draft.trades = EMPTY_ARRAY
        draft.actionsBase = null
        break
      case "setTradedPosition":
        draft.tradedPosition = action.tradedPosition
        draft.tradedPositionField = action.tradedPositionField
        break
      case "setExpanded":
        draft.isExpanded = action.isExpanded
        break
      case "setAssetViewGroupBy":
        draft.assetViewGroupBy = action.assetViewGroupBy
        break
      case "setPreferences":
        if (action.preferences) {
          Object.assign(draft, action.preferences)
        }
        break
      case "updateTradeFilters":
        if (!isEmpty(draft.trades)) {
          draft.trades.forEach(trade => {
            trade.filter = action.filter
          })
        }
        break
    }
  })
}

function addTrade(trade) {
  return {
    type: "addTrade",
    trade,
  }
}

function resetTrades() {
  return {
    type: "resetTrades",
  }
}

function setTradedPosition(tradedPosition, tradedPositionField) {
  return {
    type: "setTradedPosition",
    tradedPosition,
    tradedPositionField,
  }
}

function setExpanded(isExpanded) {
  return {
    type: "setExpanded",
    isExpanded,
  }
}

function setAssetViewGroupBy(assetViewGroupBy) {
  return {
    type: "setAssetViewGroupBy",
    assetViewGroupBy: assetViewGroupBy,
  }
}

function setPreferences(preferences) {
  return {
    type: "setPreferences",
    preferences,
  }
}

function updateTradeFilters(filter) {
  return {
    type: "updateTradeFilters",
    filter,
  }
}

export default function useTradeContext(parentContext) {
  const parentContextValue = useContext(parentContext)
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE)

  const isPreferencesLoaded = usePreferences(
    PREFERENCE_SUBTYPE,
    PREFERENCE_FIELDS,
    state,
    dispatch,
    setPreferences,
  )

  const contextUpdaters = useMemo(() => ({
    addTrade: (...args) => dispatch(addTrade(...args)),
    resetTrades: () => dispatch(resetTrades()),
    setTradedPosition: (...args) => dispatch(setTradedPosition(...args)),
    setExpanded: (...args) => dispatch(setExpanded(...args)),
    updateTradeFilters: (...args) => dispatch(updateTradeFilters(...args)),
    setAssetViewGroupBy: (...args) => dispatch(setAssetViewGroupBy(...args)),
  }), [dispatch])

  const contextValue = useMemo(() => ({
    ...parentContextValue,
    ...state,
    ...contextUpdaters,
    isPreferencesLoaded,
  }), [contextUpdaters, state, parentContextValue, isPreferencesLoaded])

  return [TradeContext, contextValue]
}
